import { useState, useEffect, useCallback, useRef } from "react";
import { hc } from "@/frontend/lib/hc";
import { captureError } from "@/frontend/lib/error-tracking";

type NotificationPermission = "granted" | "denied" | "default";

interface PushSubscription {
  id: string;
  endpoint: string;
  installationId: string | null;
  userAgent: string | null;
  deviceName: string | null;
  createdAt: string;
}

interface PushNotificationState {
  permission: NotificationPermission;
  isSubscribed: boolean;
  isSupported: boolean;
  isLoading: boolean;
  error: string | null;
  subscriptions: PushSubscription[];
}

type PushActionErrorCode =
  | "unsupported"
  | "organization_required"
  | "permission_denied"
  | "permission_request"
  | "service_worker"
  | "browser_subscription"
  | "backend_save"
  | "status_refresh";

interface PushActionResult {
  success: boolean;
  error: string | null;
  code: PushActionErrorCode | null;
}

const PUSH_INSTALLATION_ID_KEY = "push-notification-installation-id";
const autoSyncInFlight = new Map<string, Promise<Response>>();
const autoSyncGeneration = new Map<string, number>();

function autoSyncKey(organizationId: string, userId: string): string {
  return `${organizationId}:${userId}`;
}

function getAutoSyncGeneration(organizationId: string, userId: string): number {
  return autoSyncGeneration.get(autoSyncKey(organizationId, userId)) ?? 0;
}

function cancelAutoSync(organizationId: string, userId: string | null): void {
  if (!userId) return;
  const key = autoSyncKey(organizationId, userId);
  autoSyncGeneration.set(key, (autoSyncGeneration.get(key) ?? 0) + 1);
}

async function waitForAutoSync(
  organizationId: string,
  userId: string | null,
): Promise<void> {
  if (!userId) return;
  const pending = autoSyncInFlight.get(autoSyncKey(organizationId, userId));
  if (!pending) return;
  try {
    await pending;
  } catch {
    // The explicit user action still needs to run after a failed reconciliation.
  }
}

function pushPreferenceKey(organizationId: string, userId: string): string {
  return `push-notifications-enabled-${organizationId}-${userId}`;
}

function getPushPreference(
  organizationId: string,
  userId: string | null,
): boolean | null {
  if (!userId) return null;

  let value: string | null;
  try {
    value = window.localStorage.getItem(
      pushPreferenceKey(organizationId, userId),
    );
  } catch {
    return null;
  }
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

function setPushPreference(
  organizationId: string,
  userId: string | null,
  enabled: boolean,
): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(
      pushPreferenceKey(organizationId, userId),
      String(enabled),
    );
  } catch {
    // Push still works without durable reconciliation preferences.
  }
}

function restorePushPreference(
  organizationId: string,
  userId: string | null,
  preference: boolean | null,
): void {
  if (!userId) return;
  try {
    if (preference === null) {
      window.localStorage.removeItem(pushPreferenceKey(organizationId, userId));
    } else {
      window.localStorage.setItem(
        pushPreferenceKey(organizationId, userId),
        String(preference),
      );
    }
  } catch {
    // Storage availability must not change the result of the server operation.
  }
}

function getOrCreateInstallationId(): string | null {
  try {
    const existing = window.localStorage.getItem(PUSH_INSTALLATION_ID_KEY);
    if (existing) return existing;

    const installationId = globalThis.crypto.randomUUID();
    window.localStorage.setItem(PUSH_INSTALLATION_ID_KEY, installationId);
    return installationId;
  } catch {
    return null;
  }
}

async function saveBrowserSubscription(
  organizationId: string,
  subscription: globalThis.PushSubscription,
  source: "manual" | "reconcile",
  installationId: string | null,
): Promise<Response> {
  const subscriptionJSON = subscription.toJSON();
  return hc.api.push.subscribe.$post(
    {
      json: {
        subscription: {
          endpoint: subscriptionJSON.endpoint!,
          keys: {
            p256dh: subscriptionJSON.keys!.p256dh!,
            auth: subscriptionJSON.keys!.auth!,
          },
        },
        userAgent: navigator.userAgent,
        deviceName: generateDeviceName(),
        installationId: installationId ?? undefined,
        source,
      },
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    },
  ) as Promise<Response>;
}

async function autoSyncBrowserSubscription(
  organizationId: string,
  userId: string,
  subscription: globalThis.PushSubscription,
  installationId: string | null,
  expectedGeneration: number,
): Promise<Response | null> {
  const key = autoSyncKey(organizationId, userId);
  if (getAutoSyncGeneration(organizationId, userId) !== expectedGeneration) {
    return null;
  }

  const existing = autoSyncInFlight.get(key);
  if (existing) return existing;

  const pending = saveBrowserSubscription(
    organizationId,
    subscription,
    "reconcile",
    installationId,
  ).finally(() => {
    if (autoSyncInFlight.get(key) === pending) {
      autoSyncInFlight.delete(key);
    }
  });
  autoSyncInFlight.set(key, pending);
  return pending;
}

export function isCurrentDeviceSubscribed(
  subscriptions: PushSubscription[],
  endpoint: string | null,
): boolean {
  return endpoint !== null && subscriptions.some((sub) => sub.endpoint === endpoint);
}

export function usePushNotifications(
  organizationId: string | null,
  userId: string | null = null,
) {
  const statusRequestIdRef = useRef(0);
  const [state, setState] = useState<PushNotificationState>({
    permission: "default",
    isSubscribed: false,
    isSupported: false,
    isLoading: true,
    error: null,
    subscriptions: [],
  });

  // Check if push notifications are supported
  useEffect(() => {
    const isSupported =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;

    setState((prev) => ({ ...prev, isSupported, isLoading: false }));

    if (isSupported && "Notification" in window) {
      setState((prev) => ({
        ...prev,
        permission: Notification.permission as NotificationPermission,
      }));
    }
  }, []);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!organizationId) return false;
    const requestId = ++statusRequestIdRef.current;
    const isStale = () => statusRequestIdRef.current !== requestId;

    try {
      const response = await hc.api.push.status.$get(
        {},
        {
          headers: {
            "x-organization-id": organizationId,
          },
        }
      );
      if (isStale()) return true;

      if (!response.ok) {
        throw new Error("Failed to load push subscription status");
      }

      let data = await response.json();
      if (isStale()) return true;
      const registration = await navigator.serviceWorker.ready;
      if (isStale()) return true;
      let currentSubscription = await registration.pushManager.getSubscription();
      if (isStale()) return true;
      const installationId = getOrCreateInstallationId();
      const currentDevice = (data.subscriptions || []).find(
        (subscription: PushSubscription) =>
          subscription.endpoint === currentSubscription?.endpoint,
      );
      let currentDeviceSubscribed = Boolean(currentDevice);

      let preference = getPushPreference(organizationId, userId);
      if (currentDeviceSubscribed && preference === null) {
        // Preserve existing users' opt-in across the rollout of auto-sync.
        setPushPreference(organizationId, userId, true);
        preference = true;
      }

      const needsInstallationBackfill = Boolean(
        currentDeviceSubscribed &&
        installationId &&
        currentDevice?.installationId !== installationId,
      );
      if (
        preference === true &&
        userId &&
        Notification.permission === "granted" &&
        (!currentDeviceSubscribed || needsInstallationBackfill)
      ) {
        const hadExistingSubscription = Boolean(currentSubscription);
        const expectedGeneration = getAutoSyncGeneration(organizationId, userId);
        try {
          if (!currentSubscription) {
            const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
            if (!vapidPublicKey) {
              throw new Error("VAPID public key not configured");
            }
            currentSubscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });
            if (isStale()) return true;
          }

          const syncResponse = await autoSyncBrowserSubscription(
            organizationId,
            userId,
            currentSubscription,
            installationId,
            expectedGeneration,
          );
          if (isStale()) return true;
          if (!syncResponse) {
            return true;
          }
          if (syncResponse.status === 409) {
            setPushPreference(organizationId, userId, false);
            currentDeviceSubscribed = false;
          } else if (!syncResponse.ok) {
            throw new Error("Failed to synchronize push subscription");
          }

          const refreshedResponse = await hc.api.push.status.$get(
            {},
            {
              headers: {
                "x-organization-id": organizationId,
              },
            },
          );
          if (isStale()) return true;
          if (!refreshedResponse.ok) {
            throw new Error("Failed to refresh synchronized push subscription");
          }
          data = await refreshedResponse.json();
          if (isStale()) return true;
          currentDeviceSubscribed = isCurrentDeviceSubscribed(
            data.subscriptions || [],
            currentSubscription.endpoint,
          );
        } catch (error) {
          const normalizedError =
            error instanceof Error ? error : new Error(String(error));
          console.error(
            "Error synchronizing push notification subscription:",
            normalizedError,
          );
          captureError(normalizedError, {
            component: "usePushNotifications",
            action: "auto_sync",
            metadata: {
              organizationId,
              notificationPermission: Notification.permission,
              hadExistingSubscription,
            },
          });
        }
      }

      if (isStale()) return true;
      setState((prev) => ({
        ...prev,
        isSubscribed: currentDeviceSubscribed,
        subscriptions: data.subscriptions || [],
      }));
      return true;
    } catch (error) {
      if (isStale()) return true;
      console.error("Error checking subscription status:", error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Failed to load push subscription status",
      }));
      return false;
    }
  }, [organizationId, userId]);

  // Check subscription status when org changes
  useEffect(() => {
    if (!state.isSupported || !organizationId) return;

    checkSubscriptionStatus();
    return () => {
      statusRequestIdRef.current += 1;
    };
  }, [organizationId, state.isSupported, checkSubscriptionStatus]);

  const subscribe = useCallback(async () => {
    if (!state.isSupported) {
      return {
        success: false,
        error: "Push notifications are not supported in this browser",
        code: "unsupported" as const,
      };
    }
    if (!organizationId) {
      return {
        success: false,
        error: "Organization ID is required",
        code: "organization_required" as const,
      };
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    let stage: Exclude<
      PushActionErrorCode,
      "unsupported" | "organization_required" | "permission_denied" | "permission_request"
    > = "service_worker";
    let hadExistingSubscription = false;
    let responseStatus: number | null = null;

    try {
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();
      hadExistingSubscription = Boolean(subscription);

      if (!subscription) {
        stage = "browser_subscription";
        // Subscribe to push notifications
        const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
        if (!vapidPublicKey) {
          throw new Error("VAPID public key not configured");
        }

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
      }

      // Send subscription to backend
      stage = "backend_save";
      const response = await saveBrowserSubscription(
        organizationId,
        subscription,
        "manual",
        getOrCreateInstallationId(),
      );

      if (!response.ok) {
        responseStatus = response.status;
        let message = "Failed to subscribe";
        try {
          const body = await response.json();
          if (typeof body.error === "string") {
            message = body.error;
          }
        } catch {
          // Keep the fallback when the server response is not JSON.
        }
        throw new Error(message);
      }

      setPushPreference(organizationId, userId, true);
      stage = "status_refresh";
      if (!(await checkSubscriptionStatus())) {
        throw new Error("Subscription saved but failed to refresh device status");
      }
      setState((prev) => ({ ...prev, isLoading: false }));
      return { success: true, error: null, code: null };
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      const displayError =
        stage === "backend_save"
          ? "Failed to save this device. Please try again."
          : normalizedError.message;
      console.error("Error subscribing to push notifications:", normalizedError);
      captureError(normalizedError, {
        component: "usePushNotifications",
        action: "subscribe",
        metadata: {
          organizationId,
          stage,
          notificationPermission: Notification.permission,
          hadExistingSubscription,
          responseStatus,
        },
      });
      setState((prev) => ({
        ...prev,
        isSubscribed: false,
        isLoading: false,
        error: displayError,
      }));
      return { success: false, error: displayError, code: stage };
    }
  }, [state.isSupported, organizationId, userId, checkSubscriptionStatus]);

  const requestPermission = useCallback(async (): Promise<PushActionResult> => {
    if (!state.isSupported) {
      const error = "Push notifications are not supported in this browser";
      setState((prev) => ({
        ...prev,
        error,
      }));
      return { success: false, error, code: "unsupported" };
    }

    if (!organizationId) {
      const error = "Organization ID is required";
      setState((prev) => ({
        ...prev,
        error,
      }));
      return { success: false, error, code: "organization_required" };
    }

    try {
      const permission = await Notification.requestPermission();
      setState((prev) => ({
        ...prev,
        permission: permission as NotificationPermission,
      }));

      if (permission === "granted") {
        return await subscribe();
      } else {
        const error = "Notification permission denied";
        setState((prev) => ({
          ...prev,
          error,
        }));
        return { success: false, error, code: "permission_denied" };
      }
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      console.error("Error requesting permission:", normalizedError);
      captureError(normalizedError, {
        component: "usePushNotifications",
        action: "request_permission",
        metadata: { organizationId },
      });
      setState((prev) => ({
        ...prev,
        error: normalizedError.message,
      }));
      return {
        success: false,
        error: normalizedError.message,
        code: "permission_request",
      };
    }
  }, [state.isSupported, organizationId, subscribe]);

  const unsubscribe = useCallback(async () => {
    if (!state.isSupported || !organizationId) return false;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    const previousPreference = getPushPreference(organizationId, userId);
    let backendDisabled = false;
    setPushPreference(organizationId, userId, false);
    cancelAutoSync(organizationId, userId);

    try {
      await waitForAutoSync(organizationId, userId);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove subscription from backend
        const response = await hc.api.push.unsubscribe.$delete(
          {
            query: {
              endpoint: subscription.endpoint,
            },
          },
          {
            headers: {
              "x-organization-id": organizationId,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to remove subscription from backend");
        }
      }
      backendDisabled = true;

      if (!(await checkSubscriptionStatus())) {
        throw new Error("Unsubscribed but failed to refresh device status");
      }
      setState((prev) => ({ ...prev, isLoading: false }));
      return true;
    } catch (error) {
      if (!backendDisabled) {
        restorePushPreference(organizationId, userId, previousPreference);
      }
      console.error("Error unsubscribing from push notifications:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to unsubscribe",
      }));
      return false;
    }
  }, [state.isSupported, organizationId, userId, checkSubscriptionStatus]);

  const sendTestNotification = useCallback(async () => {
    if (!organizationId) return false;

    try {
      const response = await hc.api.push.test.$post(
        {
          json: {
            title: "Test Notification",
            body: "This is a test push notification from QueryGen!",
          },
        },
        {
          headers: {
            "x-organization-id": organizationId,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send test notification");
      }

      return true;
    } catch (error) {
      console.error("Error sending test notification:", error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Failed to send test notification",
      }));
      return false;
    }
  }, [organizationId]);

  const deleteSubscription = useCallback(async (subscriptionId: string) => {
    if (!organizationId) return false;

    const deletedSubscription = state.subscriptions.find(
      (subscription) => subscription.id === subscriptionId,
    );
    let deletingCurrentDevice = false;
    let previousPreference: boolean | null = null;
    let backendDisabled = false;

    try {
      if (deletedSubscription) {
        const registration = await navigator.serviceWorker.ready;
        const currentSubscription =
          await registration.pushManager.getSubscription();
        deletingCurrentDevice =
          currentSubscription?.endpoint === deletedSubscription.endpoint;
        if (deletingCurrentDevice) {
          previousPreference = getPushPreference(organizationId, userId);
          setPushPreference(organizationId, userId, false);
          cancelAutoSync(organizationId, userId);
          await waitForAutoSync(organizationId, userId);
        }
      }

      const response = await hc.api.push.subscription[":id"].$delete(
        {
          param: { id: subscriptionId },
        },
        {
          headers: {
            "x-organization-id": organizationId,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete subscription");
      }
      backendDisabled = true;

      // Refresh subscription status
      await checkSubscriptionStatus();
      return true;
    } catch (error) {
      if (deletingCurrentDevice && !backendDisabled) {
        restorePushPreference(organizationId, userId, previousPreference);
      }
      console.error("Error deleting subscription:", error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Failed to delete subscription",
      }));
      return false;
    }
  }, [organizationId, userId, state.subscriptions, checkSubscriptionStatus]);

  return {
    ...state,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification,
    checkSubscriptionStatus,
    deleteSubscription,
  };
}

// Helper function to convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Helper function to generate device name from user agent
export function getPushDeviceName(ua: string): string {
  // Detect browser
  let browser = "Browser";
  if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Edg")) browser = "Edge";

  // Detect OS
  let os = "Unknown";
  if (ua.includes("Win")) os = "Windows";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  else if (ua.includes("Mac")) os = "Mac";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("Linux")) os = "Linux";

  return `${browser} on ${os}`;
}

function generateDeviceName(): string {
  return getPushDeviceName(navigator.userAgent);
}
